// Feb 20, 2026 — VoucherTopupModal.
//
// Smart multi-silo top-up modal. Lets the creator pick how much to fund
// each of their silos in one Stripe Checkout session. Pre-fills each row
// with the silo's last-30-day platform-key spend (with a $5 minimum) as
// the "recommended" amount. The creator can edit per row, zero out silos
// they don't want to fund, or just hit "Use recommended" to autofill.
//
// Validates total ≥ $5. No max ceiling per creator's instruction.
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { X, Sparkle, CurrencyDollar, Info, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtUSD = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function VoucherTopupModal({ open, onClose, onSuccess, prefocusedSiloId = null, defaultProvider = null }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Per-silo dollar string. e.g. {silo_id: "5.00"}
  const [draft, setDraft] = useState({});
  // Feb 28, 2026 — Which provider bucket the cents land in. null = split
  // evenly across all three (legacy fallback). Set via `defaultProvider`
  // when the top-up CTA was provider-specific.
  const [topupProvider, setTopupProvider] = useState(defaultProvider);
  useEffect(() => { setTopupProvider(defaultProvider); }, [defaultProvider]);

  // May 25, 2026 — Inter-silo transfer panel state. Only shown when a
  // specific target silo is pre-focused (otherwise "fund X from Y" is
  // ambiguous). User picks per-source amounts; we keep each source's
  // voucher >= $1.00 floor server-side.
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferSources, setTransferSources] = useState(null);
  const [transferDraft, setTransferDraft] = useState({}); // {source_id: "0.25"}
  const [transferBusy, setTransferBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/me/voucher-summary");
        if (cancelled) return;
        setSummary(r.data);
        // Pre-fill: recommended cents for the prefocused silo, $0 for others
        // (creator deliberately picks who to fund). If no prefocus, all
        // pre-filled with recommended.
        const init = {};
        for (const s of r.data.silos) {
          if (prefocusedSiloId && s.silo_id !== prefocusedSiloId) {
            init[s.silo_id] = "0.00";
          } else {
            init[s.silo_id] = (Math.max(s.recommended_topup_cents, 500) / 100).toFixed(2);
          }
        }
        setDraft(init);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't load silos.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, prefocusedSiloId]);

  // Load eligible transfer sources lazily when the panel opens.
  useEffect(() => {
    if (!transferOpen || !prefocusedSiloId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/silos/${prefocusedSiloId}/voucher/transfer-sources`);
        if (cancelled) return;
        setTransferSources(r.data);
        const init = {};
        for (const s of r.data.sources) init[s.silo_id] = "0.00";
        setTransferDraft(init);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't load transfer sources.");
      }
    })();
    return () => { cancelled = true; };
  }, [transferOpen, prefocusedSiloId]);

  const transferTotalCents = useMemo(() => {
    return Object.values(transferDraft).reduce((acc, v) => {
      const n = parseFloat(v || "0");
      if (Number.isNaN(n) || n < 0) return acc;
      return acc + Math.round(n * 100);
    }, 0);
  }, [transferDraft]);

  const submitTransfer = async () => {
    if (!prefocusedSiloId) return;
    const sources = Object.entries(transferDraft)
      .map(([silo_id, v]) => {
        const n = parseFloat(v || "0");
        if (Number.isNaN(n) || n <= 0) return null;
        return { silo_id, amount_cents: Math.round(n * 100) };
      })
      .filter(Boolean);
    if (!sources.length) {
      toast.error("Type an amount next to at least one silo.");
      return;
    }
    setTransferBusy(true);
    try {
      const r = await api.post(`/silos/${prefocusedSiloId}/voucher/transfer-in`, { sources });
      const moved = ((r.data?.total_moved_cents || 0) / 100).toFixed(2);
      toast.success(`Pulled $${moved} in. New balance: $${((r.data?.target_voucher_cents || 0) / 100).toFixed(2)}.`);
      // Refresh summary + reset transfer panel
      const re = await api.get("/me/voucher-summary");
      setSummary(re.data);
      setTransferOpen(false);
      setTransferDraft({});
      setTransferSources(null);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer failed.");
    } finally {
      setTransferBusy(false);
    }
  };

  const totalCents = useMemo(() => {
    return Object.values(draft).reduce((acc, v) => {
      const n = parseFloat(v || "0");
      if (Number.isNaN(n) || n < 0) return acc;
      return acc + Math.round(n * 100);
    }, 0);
  }, [draft]);

  const allocations = useMemo(() => {
    return Object.entries(draft)
      .map(([silo_id, v]) => {
        const n = parseFloat(v || "0");
        if (Number.isNaN(n) || n <= 0) return null;
        return { silo_id, amount_cents: Math.round(n * 100), provider: topupProvider };
      })
      .filter(Boolean);
  }, [draft, topupProvider]);

  const submit = async () => {
    if (totalCents < 500) {
      toast.error("Minimum top-up is $5 total across all silos.");
      return;
    }
    if (!allocations.length) {
      toast.error("Pick at least one silo to fund.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/vouchers/topup-checkout", { allocations });
      if (r.data?.url) {
        window.location.href = r.data.url; // off to Stripe
      } else {
        toast.error("Couldn't open checkout.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed.");
    } finally {
      setBusy(false);
    }
  };

  // Jun 2026 (Randy) — top up BEFORE you have a silo. The credit is held on
  // your account and auto-lands in your first silo when you create it.
  const submitGeneral = async (cents, provider) => {
    if (cents < 500) { toast.error("Minimum top-up is $5."); return; }
    setBusy(true);
    try {
      const r = await api.post("/vouchers/topup-checkout", {
        allocations: [{ silo_id: "__general__", amount_cents: cents, provider: provider || null }],
      });
      if (r.data?.url) window.location.href = r.data.url;
      else toast.error("Couldn't open checkout.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed.");
    } finally { setBusy(false); }
  };

  const useRecommended = () => {
    if (!summary) return;
    const next = { ...draft };
    for (const s of summary.silos) {
      next[s.silo_id] = (Math.max(s.recommended_topup_cents, 500) / 100).toFixed(2);
    }
    setDraft(next);
  };

  const clearAll = () => {
    const next = { ...draft };
    for (const k of Object.keys(next)) next[k] = "0.00";
    setDraft(next);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="voucher-topup-modal">
      <div className="bg-[#FDFCF8] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border hairline"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#FDFCF8] border-b hairline px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkle size={18} weight="duotone" className="text-[#4A5D4E]" />
            <h2 className="font-serif-display text-xl text-[#1C1B1A]">Top up platform credit</h2>
          </div>
          <button onClick={onClose} data-testid="voucher-modal-close"
            className="p-1 rounded hover:bg-[#F0F1EE] text-[#6B6A68] hover:text-[#1C1B1A]">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Oct 2026 (TRIAL_PLAN.md §7.3 item 9) — what the money actually buys
              while a trial is running, and why BYOK exists at all. */}
          {(summary?.ghost_trial_cents > 0 || summary?.ghost_comp_cents > 0) && (
            <div className="bg-[#E8EBE5] border border-[#4A5D4E]/30 rounded-md p-3 text-xs text-[#4A5D4E]"
              data-testid="voucher-topup-trial-note">
              <strong>
                You have {fmtUSD(summary.ghost_trial_cents || summary.ghost_comp_cents)} of{" "}
                {summary.ghost_trial_cents > 0 ? "trial" : "gifted"} credit left.
              </strong>{" "}
              It's spent first, at cost (1×), and it can't be cashed out.
              {summary.ghost_trial_cents > 0 && (
                <span className="block mt-1 text-[11px] text-[#1C1B1A]">
                  During your trial, platform-key calls are billed at cost (1×) — including anything you
                  top up now, for as long as that credit lasts. After the trial, platform-key calls carry
                  a 3× convenience margin. Bring your own Gemini key and you pay Google directly, forever.
                </span>
              )}
            </div>
          )}
          {/* Feb 28, 2026 — Provider-targeted top-up banner. When a specific
              provider bucket is in focus (e.g. the user clicked "Top up
              Anthropic" from the dashboard tracker), nudge them toward the
              3× cheaper BYOK alternative while still letting them complete
              the top-up. */}
          {topupProvider && (
            <div className="bg-[#FBE9E1] border border-[#D9A23A] rounded-md p-3 text-xs text-[#7A5200]"
              data-testid="voucher-topup-provider-banner">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <strong>Funding the {topupProvider} bucket.</strong> Top-ups here pay <strong>3× the raw {topupProvider} price</strong> for platform calls.
                  <span className="block mt-1 text-[11px] text-[#4A5D4E]">
                    For 3× cheaper, set your own personal {topupProvider} BYOK in Account &amp; Billing and get off the Site key for {topupProvider} altogether.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setTopupProvider(null)}
                  data-testid="voucher-topup-clear-provider"
                  className="text-[10px] uppercase tracking-[0.15em] text-[#6B6A68] hover:text-[#1C1B1A] underline shrink-0"
                >
                  Split evenly instead
                </button>
              </div>
            </div>
          )}

          <div className="text-xs text-[#6B6A68] bg-[#F0F1EE] border hairline rounded-md p-3 flex gap-2">
            <Info size={14} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
            <div>
              <strong>Platform credit</strong> is a small pre-paid balance that keeps a silo&apos;s AI features running when no personal API key is on file. Each silo has its own voucher, so you can fund only the ones you actively use.
              <br />
              If a silo runs out of platform credit <em>and</em> doesn&apos;t have a personal Gemini/Claude/OpenAI key attached, its AI features quietly pause until you top it up or add a key.
              <br />
              <span className="text-[10px] italic text-[#4A5D4E]">Recommendations below = each silo&apos;s platform-key spend over the last 30 days. Edit any row freely.</span>
            </div>
          </div>

          {loading || !summary ? (
            <div className="text-sm text-[#6B6A68] py-8 text-center">Loading your silos…</div>
          ) : summary.silos.length === 0 ? (
            <GeneralTopup busy={busy} onSubmit={submitGeneral} heldCents={summary?.held_topup_cents || 0} />
          ) : (
            <>
              {/* May 25, 2026 — Inter-silo transfer panel. Pre-Stripe shortcut:
                  pull funds from silos you already own into the target. No
                  card-swipe needed, settles instantly. Only available when a
                  single target silo is in focus (otherwise "fund X from Y" is
                  ambiguous). */}
              {prefocusedSiloId && (
                <TransferPanel
                  open={transferOpen}
                  onToggle={() => setTransferOpen((v) => !v)}
                  sources={transferSources}
                  draft={transferDraft}
                  setDraft={setTransferDraft}
                  totalCents={transferTotalCents}
                  busy={transferBusy}
                  onSubmit={submitTransfer}
                />
              )}

              <div className="space-y-2">
                {summary.silos.map((s) => (
                  <SiloRow
                    key={s.silo_id}
                    silo={s}
                    value={draft[s.silo_id] || "0.00"}
                    onChange={(v) => setDraft((d) => ({ ...d, [s.silo_id]: v }))}
                  />
                ))}
              </div>

              <div className="flex gap-2 text-[11px]">
                <button onClick={useRecommended} data-testid="voucher-use-recommended"
                  className="px-3 py-1.5 rounded border hairline bg-white text-[#4A5D4E] hover:border-[#4A5D4E]">
                  Use recommended for all
                </button>
                <button onClick={clearAll} data-testid="voucher-clear-all"
                  className="px-3 py-1.5 rounded border hairline bg-white text-[#6B6A68] hover:text-[#1C1B1A]">
                  Clear all
                </button>
              </div>

              <div className="flex items-center justify-between pt-3 border-t hairline">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Total</div>
                  <div className={`font-serif-display text-2xl tabular-nums ${totalCents < 500 ? "text-[#C16E5A]" : "text-[#1C1B1A]"}`}
                    data-testid="voucher-topup-total">
                    {fmtUSD(totalCents)}
                  </div>
                  {totalCents < 500 && (
                    <div className="text-[10px] text-[#C16E5A]">Minimum $5</div>
                  )}
                </div>
                <button
                  onClick={submit}
                  disabled={busy || totalCents < 500}
                  data-testid="voucher-checkout-btn"
                  className="px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <CurrencyDollar size={14} weight="bold" />
                  {busy ? "Opening Stripe…" : `Pay ${fmtUSD(totalCents)}`}
                </button>
              </div>

              <VoucherCheckoutDisclosure />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Randy-approved checkout disclosure (Draft 1 of the four voucher policy
// drafts, Jun 2026). Keep in step with VoucherHeldStrip, /refund-policy and
// the Terms — the three must never say different things about the same money.
export function VoucherCheckoutDisclosure() {
  return (
    <div
      className="mt-4 pt-3 border-t hairline text-[10px] leading-relaxed text-[#6B6A68] space-y-2"
      data-testid="voucher-checkout-disclosure"
    >
      <div>
        <span className="uppercase tracking-[0.16em] text-[#1C1B1A]">What you're buying</span>
        <br />
        You're adding prepaid AI credit to your silos. Credit is spent as you use AI features
        and does not expire.
      </div>
      <div>
        <span className="uppercase tracking-[0.16em] text-[#1C1B1A]">Refunds</span>
        <br />
        You can ask for a refund of any credit you haven't used yet, and we'll return it to the
        card you paid with. Credit that has already been spent on AI usage, or withdrawn as cash,
        can't be refunded — the cost was already incurred on our side.
      </div>
      <div>
        <span className="uppercase tracking-[0.16em] text-[#1C1B1A]">If your bank opens a dispute</span>
        <br />
        Credit from a disputed payment is held while the dispute is open. Held credit stays on
        your account but can't be spent, moved or withdrawn until the dispute closes. If it closes
        in your favor, the credit is released automatically.
      </div>
      <div>
        <Link to="/refund-policy" className="underline hover:text-[#1C1B1A]"
          data-testid="voucher-checkout-refund-policy-link">Refund policy</Link>
        {" · "}
        <Link to="/terms" className="underline hover:text-[#1C1B1A]"
          data-testid="voucher-checkout-terms-link">Terms</Link>
      </div>
    </div>
  );
}

function GeneralTopup({ busy, onSubmit, heldCents = 0 }) {
  const [amount, setAmount] = useState("10.00");
  const [provider, setProvider] = useState(null); // null = split evenly
  const cents = Math.round(parseFloat(amount || "0") * 100) || 0;
  const PROVS = [
    { id: null, label: "Split evenly" },
    { id: "gemini", label: "Gemini" },
    { id: "anthropic", label: "Claude" },
    { id: "openai", label: "OpenAI" },
  ];
  return (
    <div className="space-y-4" data-testid="voucher-general-topup">
      {heldCents > 0 && (
        <div className="bg-[#E9F3EC] border border-[#2F8F4E]/40 rounded-md p-3 text-sm text-[#2F6B3A] flex items-start gap-2" data-testid="general-held-note">
          <CheckCircle size={16} weight="duotone" className="text-[#2F8F4E] shrink-0 mt-0.5" />
          <div>
            You already have <strong>${(heldCents / 100).toFixed(2)}</strong> held and ready — it lands in your first silo the moment you create one. No need to top up again unless you want more.
          </div>
        </div>
      )}
      <div className="bg-[#E8EBE5] border border-[#4A5D4E]/30 rounded-md p-3 text-xs text-[#3A4A3E]">
        You don&apos;t have a silo yet — no problem. Add credit now and we&apos;ll hold it for you, then drop it straight into your first silo the moment you create one. Nothing gets lost.
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Provider bucket</div>
        <div className="flex flex-wrap gap-1.5">
          {PROVS.map((p) => (
            <button key={String(p.id)} type="button"
              onClick={() => setProvider(p.id)}
              data-testid={`general-provider-${p.id || "split"}`}
              className={`text-xs px-3 py-1.5 rounded-full border hairline ${provider === p.id ? "bg-[#1C1B1A] text-[#FDFCF8] border-[#1C1B1A]" : "bg-white text-[#3A4A3E] hover:border-[#4A5D4E]"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between pt-3 border-t hairline">
        <div className="flex items-center gap-1.5">
          <span className="text-lg text-[#6B6A68]">$</span>
          <input type="number" min="5" step="1" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="general-topup-input"
            className="w-28 px-2 py-1.5 border hairline rounded text-lg text-right tabular-nums bg-white focus:outline-none focus:border-[#4A5D4E]" />
        </div>
        <button onClick={() => onSubmit(cents, provider)} disabled={busy || cents < 500}
          data-testid="general-topup-checkout-btn"
          className="px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
          <CurrencyDollar size={14} weight="bold" />
          {busy ? "Opening Stripe…" : `Pay ${fmtUSD(cents)}`}
        </button>
      </div>
      {cents < 500 && <div className="text-[10px] text-[#C16E5A] text-right">Minimum $5</div>}
      <VoucherCheckoutDisclosure />
    </div>
  );
}

function SiloRow({ silo, value, onChange }) {
  const recommended = (Math.max(silo.recommended_topup_cents, 500) / 100).toFixed(2);
  const current = silo.voucher_cents || 0;
  return (
    <div className="bg-white border hairline rounded-md p-3 flex items-center gap-3" data-testid={`voucher-row-${silo.silo_id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#1C1B1A] truncate">{silo.silo_name}</div>
        <div className="text-[10px] text-[#6B6A68] tabular-nums">
          Current voucher: {fmtUSD(current)} · 30d spend: {fmtUSD(silo.spent_last_30d_cents)}
          {silo.recommended_topup_cents >= 500 && (
            <> · Recommended: <button
              onClick={() => onChange(recommended)}
              className="underline text-[#4A5D4E] hover:text-[#1C1B1A]"
              data-testid={`voucher-row-recommended-${silo.silo_id}`}
            >${recommended}</button></>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <span className="text-sm text-[#6B6A68]">$</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`voucher-row-input-${silo.silo_id}`}
          className="w-24 px-2 py-1.5 border hairline rounded text-sm text-right tabular-nums bg-white focus:outline-none focus:border-[#4A5D4E]"
        />
      </div>
    </div>
  );
}


// =====================================================================
// TransferPanel — pull funds from other silos
// =====================================================================
// May 25, 2026. The collapsed state is a single button ("Pull from
// another silo"). Expanded shows every eligible source (other silos the
// creator owns with voucher > $1.00). Each row has its own dollar input.
// "Available" tells the creator how much they can pull without dropping
// the source below the $1 floor. Hit "Pull funds in" to fire the
// transfer-in endpoint and refresh.
function TransferPanel({ open, onToggle, sources, draft, setDraft, totalCents, busy, onSubmit }) {
  return (
    <div className="border hairline rounded-md bg-[#FDFCF8]" data-testid="transfer-panel">
      <button
        type="button"
        onClick={onToggle}
        data-testid="transfer-panel-toggle"
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-[#F0F1EE] transition-colors"
      >
        <div className="flex items-center gap-2">
          <CurrencyDollar size={14} weight="duotone" className="text-[#4A5D4E]" />
          <span className="text-sm text-[#1C1B1A]">
            {open ? "Hide transfer panel" : "Pull funds from another silo"}
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
          {open ? "−" : "+"} Skip Stripe
        </span>
      </button>
      {open && (
        <div className="border-t hairline p-3 space-y-3 bg-white" data-testid="transfer-panel-body">
          {!sources ? (
            <div className="text-xs text-[#6B6A68] py-3 text-center">Loading eligible silos…</div>
          ) : sources.sources.length === 0 ? (
            <div className="text-xs text-[#6B6A68] py-3 text-center">
              No other silo you own has more than ${(sources.floor_cents / 100).toFixed(2)} in its voucher. Top up one first to enable transfers.
            </div>
          ) : (
            <>
              <p className="text-[11px] text-[#6B6A68] leading-snug">
                Move cents from any of your other silos into this one. Each source keeps at least <strong>${(sources.floor_cents / 100).toFixed(2)}</strong> as a safety floor — you can&apos;t accidentally drain it to zero.
              </p>
              <div className="space-y-2">
                {sources.sources.map((s) => (
                  <div key={s.silo_id} className="flex items-center gap-3" data-testid={`transfer-row-${s.silo_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1C1B1A] truncate">{s.silo_name}</div>
                      <div className="text-[10px] text-[#6B6A68] tabular-nums">
                        Has ${(s.voucher_cents / 100).toFixed(2)} ·{" "}
                        <button
                          type="button"
                          onClick={() => setDraft((d) => ({ ...d, [s.silo_id]: (s.available_cents / 100).toFixed(2) }))}
                          className="underline text-[#4A5D4E] hover:text-[#1C1B1A]"
                          data-testid={`transfer-row-max-${s.silo_id}`}
                        >
                          Max ${(s.available_cents / 100).toFixed(2)}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-sm text-[#6B6A68]">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        max={(s.available_cents / 100).toFixed(2)}
                        value={draft[s.silo_id] || "0.00"}
                        onChange={(e) => setDraft((d) => ({ ...d, [s.silo_id]: e.target.value }))}
                        data-testid={`transfer-row-input-${s.silo_id}`}
                        className="w-24 px-2 py-1.5 border hairline rounded text-sm text-right tabular-nums bg-white focus:outline-none focus:border-[#4A5D4E]"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2 border-t hairline">
                <div className="text-[11px] text-[#6B6A68]">
                  Pulling in:{" "}
                  <span className="font-medium text-[#1C1B1A] tabular-nums" data-testid="transfer-total">
                    ${(totalCents / 100).toFixed(2)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={busy || totalCents <= 0}
                  data-testid="transfer-submit-btn"
                  className="px-4 py-1.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#1C1B1A] disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-[0.18em]"
                >
                  {busy ? "Moving…" : "Pull funds in"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
